import React, { useEffect, useState } from "react";
import { useBrandStore } from "../store/brandStore";
import { useBrandMemberSegmentStore } from "../store/brandMemberSegmentStore";
import { brandMemberSegmentService } from "../services/brandMemberSegmentService";
import {
  Button,
  Table,
  Card,
  Row,
  Col,
  Select,
  Input,
  Modal,
  Pagination,
  Tooltip,
  Divider,
} from "antd";
import {
  PlusCircleOutlined,
  DeleteFilled,
  PlusSquareOutlined,
  MinusSquareOutlined,
  WarningFilled,
  InfoCircleFilled,
  CloseCircleOutlined,
  SaveFilled,
  EditOutlined,
} from "@ant-design/icons";
import CustomModal from "../components/common/Modal";
import { DataState, RFMTier, RFMTierDescriptions } from "../constants/enums";
import {
  BrandMemberSegmentDraft,
  BrandMemberSegmentDetailDraft,
  BrandMemberSegmentDetail,
  BrandMemberSegment,
} from "../models/brandMemberSegment";
import moment from "moment";

type ErrorDetails = {
  Recency?: string;
  Frequency?: string;
  Monetary?: string;
};

const RFMCombination: React.FC = () => {
  // =====================
  // State Declarations
  // =====================`

  // Hooks & Store
  const { brandId } = useBrandStore();
  const { brandMemberSegmentDraft, setBrandMemberSegmentDraft } =
    useBrandMemberSegmentStore();

  // UI & State Management
  const [loading, setLoading] = useState(false);
  const [isEditSection, setIsEditSection] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [assignTo, setAssignTo] = useState();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [defaultSetting, setDefaultSetting] = useState<any>([]);
  const isDisabled = !hasUnsavedChanges;

  // Modals & Notifications
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteModalDetailVisible, setDeleteModalDetailVisible] =
    useState(false);
  const [isDuplicateRFMVisible, setIsDuplicateRFMVisible] = useState(false);
  const [isCancelWarningVisible, setIsCancelWarningVisible] = useState(false);
  const [isSaveWarningVisible, setSaveWarningVisible] = useState(false);
  const [isDuplicateWarningVisible, setDuplicateWarningVisible] =
    useState(false);
  const [isModalSuccessVisible, setIsModalSuccessVisible] = useState(false);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [successTitle, setSuccessTitle] = useState("Operation Successful");
  const [successMessage, setSuccessMessage] = useState<React.ReactNode>(
    "Your action was completed successfully."
  );
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);
  const [
    isUpdateDefaultModalSuccessVisible,
    setIsUpdateDefaultModalSuccessVisible,
  ] = useState(false);

  // Selections
  const [selectedSegment, setSelectedSegment] = useState<any>(null);
  const [selectedSegmentDetail] = useState<any>(null);
  const [selectedCombationToAssign, setSelectedCombationToAssign] =
    useState<BrandMemberSegmentDetailDraft | null>(null);
  const [expandedRow, setExpandedRow] = useState<string[]>([]);

  // Data State
  const [originalBrandMemberSegment, setOriginalBrandMemberSegment] = useState<
    BrandMemberSegment[]
  >([]);
  const [deleteBrandMemberSegmentDetail, setDeleteBrandMemberSegmentDetail] =
    useState<BrandMemberSegmentDetail[]>([]);
  const [errors, setErrors] = useState<Record<number, ErrorDetails>>({});
  const [isAssignMember, setIsAssignMember] = useState(false);

  // Responsive
  const pageSize = 10;
  const isMobileOrTablet = window.innerWidth <= 1024;
  const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;

  // =====================
  // Function handler
  // =====================
  const handleEditChange = (
    id: number,
    field: "SegmentName" | "Description",
    value: string
  ) => {
    setBrandMemberSegmentDraft((prevSegments) =>
      prevSegments.map((segment) =>
        segment.Id === id
          ? {
              ...segment,
              [field]: value,
              DataState:
                segment.DataState === DataState.NO_CHANGES
                  ? DataState.UPDATED
                  : segment.DataState,
            }
          : segment
      )
    );
    setHasUnsavedChanges(true);
  };

  const handleDeleteCombination = (record: BrandMemberSegmentDetail) => {
    setHasUnsavedChanges(true);
    setDeleteBrandMemberSegmentDetail((prev) => {
      const isDuplicate = prev.some((item) => item.Id === record.Id);

      if (!isDuplicate) {
        return [...prev, record];
      }
      return prev;
    });
    setBrandMemberSegmentDraft((prevSegments: any) =>
      prevSegments.map((segment: any) => {
        if (segment.Id === record.BrandMemberSegmentId) {
          const updatedDetails = segment.BrandMemberSegmentDetails.filter(
            (detail: BrandMemberSegmentDetail) => detail.Id !== record.Id
          );

          return {
            ...segment,
            BrandMemberSegmentDetails: updatedDetails,
          };
        }

        if (segment.Id === -1) {
          return {
            ...segment,
            BrandMemberSegmentDetails: [
              ...(segment.BrandMemberSegmentDetails || []),
              record,
            ],
          };
        }

        return segment;
      })
    );
  };

  const handleCancelChanges = () => {
    setIsEditSection(false);
    setBrandMemberSegmentDraft([...originalBrandMemberSegment]);
    setHasUnsavedChanges(false);
    setDuplicateWarningVisible(false);
    setIsEditSection(false);
    setIsDuplicateRFMVisible(false);
    setIsCancelWarningVisible(false);
  };

  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      setIsCancelWarningVisible(false);
      if (duplicateDetails.length > 0 || Object.keys(errors).length > 0) {
        setIsDuplicateRFMVisible(true);
        return;
      }
      if (deleteBrandMemberSegmentDetail.length > 0) {
        await Promise.all(
          deleteBrandMemberSegmentDetail.map(async (detail: any) => {
            await brandMemberSegmentService.deleteBrandMemberSegmentDetail(
              detail.Id
            );
          })
        );
      }
      const brandMemberSegmentDraftArray = Object.values(
        brandMemberSegmentDraft
      );

      const result = await brandMemberSegmentService.BulkBrandMemberSegment(
        brandMemberSegmentDraftArray
      );
      if (result) {
        await sleep(2500); // wait for DB replication to catch up

        await fetchBrandData();
        showModalSuccess("Save Member Segment");
        setHasUnsavedChanges(false);
        setSaveWarningVisible(false);
      } else {
        showModalError();
      }
    } catch (error) {
      showModalError();
      console.error("Error saving draft:", error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      if (selectedSegment) {
        await brandMemberSegmentService.deleteBrandMemberSegment(
          selectedSegment.Id
        );
        await fetchBrandData();
        setDeleteModalVisible(false);
        setSelectedSegment(null);
        showModalSuccess("Delete Segment");
      }
    } catch (error) {
      showModalError();
      console.error("Error delete:", error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteDetail = async () => {
    if (selectedSegmentDetail) {
      await brandMemberSegmentService.deleteBrandMemberSegmentDetail(
        selectedSegmentDetail.Id
      );
      fetchBrandData();
      setDeleteModalDetailVisible(false);
    }
  };

  const handleRFMChange = (
    record: BrandMemberSegmentDetailDraft,
    field: keyof BrandMemberSegmentDetailDraft,
    value: RFMTier
  ) => {
    setBrandMemberSegmentDraft((prevSegments) =>
      prevSegments.map((segment) => {
        if (segment.Id === record.BrandMemberSegmentId) {
          return {
            ...segment,
            BrandMemberSegmentDetails: segment.BrandMemberSegmentDetails?.map(
              (detail) =>
                detail.Id === record.Id
                  ? {
                      ...detail,
                      [field]: value,
                      UpdatedDate: new Date().toISOString(),
                      DataState:
                        detail.DataState === DataState.NO_CHANGES
                          ? DataState.UPDATED
                          : detail.DataState,
                    }
                  : detail
            ),
          };
        }
        return segment;
      })
    );

    setHasUnsavedChanges(true);
  };

  const addNewSegment = () => {
    const newSegment: BrandMemberSegmentDraft = {
      Id: Math.floor(10000 + Math.random() * 90000),
      SegmentName: "New Segment",
      Description: "",
      BrandId: brandId || undefined,
      CreatedDate: new Date().toISOString(),
      BrandMemberSegmentDetails: [],
      DataState: DataState.CREATED,
    };
    setHasUnsavedChanges(true);

    const updatedSegments = [...brandMemberSegmentDraft, newSegment];
    setBrandMemberSegmentDraft(updatedSegments);
    const totalPages = Math.ceil(updatedSegments.length / pageSize);
    setCurrentPage(totalPages);

    setExpandedRow((prev: any) => [...prev, newSegment.Id]);
  };

  const addNewCombination = async (segmentId: number) => {
    await setBrandMemberSegmentDraft(
      (prevSegments: BrandMemberSegmentDraft[]) =>
        prevSegments.map((segment: BrandMemberSegmentDraft) =>
          segment.Id === segmentId
            ? {
                ...segment,
                BrandMemberSegmentDetails: [
                  ...(segment.BrandMemberSegmentDetails ?? []),
                  {
                    Id: Math.floor(10000 + Math.random() * 90000),
                    Recency: undefined,
                    Frequency: undefined,
                    Monetary: undefined,
                    BrandMemberSegmentId: segmentId,
                    DataState: DataState.CREATED,
                    UpdatedDate: new Date().toISOString(),
                  },
                ],
              }
            : segment
        )
    );
    setHasUnsavedChanges(true);
  };

  const handleExpand = (expanded: boolean, record: any) => {
    setExpandedRow((prevKeys) => {
      const updatedKeys = prevKeys ?? [];
      if (expanded) {
        return [...updatedKeys, record.Id];
      } else {
        return updatedKeys.filter((key) => key !== record.Id);
      }
    });
  };

  const HandleAssign = (record: any) => {
    setSelectedCombationToAssign(record);
    setIsAssignMember(true);
  };

  // =====================
  // Data Fetching
  // =====================
  const fetchBrandData = async () => {
    if (brandId) {
      setLoading(true);
      try {
        const data = await brandMemberSegmentService.getBrandMemberSegment(
          brandId
        );
        const processedData = (Array.isArray(data) ? data : []).map(
          (segment) => ({
            ...segment,
            DataState: DataState.NO_CHANGES,
            BrandMemberSegmentDetails: segment.BrandMemberSegmentDetails.map(
              (detail: BrandMemberSegmentDetail) => ({
                ...detail,
                DataState: DataState.NO_CHANGES,
                BrandMemberSegmentId: segment.Id,
              })
            ),
          })
        );
        setOriginalBrandMemberSegment(
          Array.isArray(processedData) ? processedData : []
        );

        setBrandMemberSegmentDraft(
          Array.isArray(processedData) ? processedData : []
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const defaultSettings = async (brandId: string) => {
    const setting =
      await brandMemberSegmentService.getDefaultBrandMemberSegment(brandId);
    return setting ?? "";
  };

  // =====================
  // Handle Duplicate
  // =====================
  const getDuplicateDetails = () => {
    const combinationMap = new Map<string, any>();
    const duplicates: any[] = [];

    for (
      let segmentIndex = 0;
      segmentIndex < brandMemberSegmentDraft.length;
      segmentIndex++
    ) {
      const segment = brandMemberSegmentDraft[segmentIndex];

      if (!segment.BrandMemberSegmentDetails) continue;
      if (segment.SegmentName === "Others") continue;

      for (
        let detailIndex = 0;
        detailIndex < segment.BrandMemberSegmentDetails.length;
        detailIndex++
      ) {
        const detail = segment.BrandMemberSegmentDetails[detailIndex];
        const key = `${detail.Recency}-${detail.Frequency}-${detail.Monetary}`;

        const detailWithIndexes = {
          ...detail,
          SegmentName: segment.SegmentName,
          ParentIndex: segmentIndex,
          DetailIndex: detailIndex,
        };

        if (!combinationMap.has(key)) {
          combinationMap.set(key, detailWithIndexes);
        } else {
          const existingDetail = combinationMap.get(key);

          const existingDate = existingDetail.UpdatedDate
            ? new Date(existingDetail.UpdatedDate).getTime()
            : 0;
          const newDate = detailWithIndexes.UpdatedDate
            ? new Date(detailWithIndexes.UpdatedDate).getTime()
            : 0;

          if (newDate >= existingDate) {
            duplicates.push({
              ...detailWithIndexes,
              conflictWith: existingDetail,
            });
          } else {
            duplicates.push({
              ...existingDetail,
              conflictWith: detailWithIndexes,
            });

            combinationMap.set(key, detailWithIndexes);
          }
        }
      }
    }

    return duplicates;
  };

  const duplicateDetails = getDuplicateDetails();

  // const validationBrandSegment = () => {
  //   const uniqueCombinations = new Set();
  //   if (!expandedRow || expandedRow > brandMemberSegmentDraft.length)
  //     return true;
  //   const segment = brandMemberSegmentDraft.find(
  //     (seg) => seg.Id === expandedRow
  //   );
  //   if (!segment || !segment.BrandMemberSegmentDetails) return true;

  //   for (const detail of segment.BrandMemberSegmentDetails) {
  //     const key = `${detail.Recency}-${detail.Frequency}-${detail.Monetary}`;

  //     if (uniqueCombinations.has(key)) {
  //       alert(
  //         "Duplicate RFM combination found. Please ensure all RFM values are unique."
  //       );
  //       return false;
  //     }
  //     uniqueCombinations.add(key);
  //   }

  //   return true;
  // };

  // =====================
  // Useeffect
  // =====================
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (brandId) fetchBrandData();
    }, 100);

    return () => clearTimeout(timeout);
  }, [brandId]);

  useEffect(() => {
    validateDraft();
  }, [brandMemberSegmentDraft]);

  useEffect(() => {
    document.body.style.overflowY = "hidden";
    window.scrollTo(0, 0);

    return () => {
      document.body.style.overflowY = "";
    };
  }, []);

  useEffect(() => {
    if (!brandId) return;

    const fetchSettings = async () => {
      const memberSetting = await defaultSettings(brandId);

      setDefaultSetting(memberSetting ?? "");
    };

    fetchSettings();
  }, [brandId]);

  // =====================
  // Columns
  // =====================

  const segmentColumns = [
    {
      title: "ID",
      dataIndex: "SegmentName",
      key: "SegmentName",
      width: "8%",
      render: (_: any, record: any, index: number) => index + 1,
    },
    {
      title: "Segment Name",
      dataIndex: "SegmentName",
      key: "SegmentName",
      width: "25%",
      render: (_: any, record: any) =>
        expandedRow.includes(record.Id) && record.Id !== -1 && isEditSection ? (
          <Input
            value={record.SegmentName}
            onChange={(e) =>
              handleEditChange(record.Id, "SegmentName", e.target.value)
            }
          />
        ) : (
          <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
            {record.SegmentName}
          </div>
        ),
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      width: isMobileOrTablet ? "45%" : "48%",
      render: (_: any, record: any) =>
        expandedRow.includes(record.Id) && record.Id !== -1 && isEditSection ? (
          <Input.TextArea
            value={record.Description}
            onChange={(e) =>
              handleEditChange(record.Id, "Description", e.target.value)
            }
          />
        ) : (
          <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
            {record.Description || "No description"}
          </div>
        ),
    },
    {
      title: "Total Members",
      dataIndex: "Count",
      key: "Count",
      width: isMobileOrTablet ? "20%" : "15%",
      render: (_: any, record: any) => {
        return record.Count
          ? `${record.Count.toLocaleString("en-US")} Members`
          : "0 Members";
      },
    },
    // {
    //   title: "Action",
    //   key: "Action",
    //   width: "10%",
    //   render: (_: any, record: any) => (
    //     <>
    //       {record.Id !== -1 && (
    //         <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
    //           {expandedRow !== record.Id && (
    //             <Button
    //               type="default"
    //               style={{
    //                 color: "#1565C0",
    //                 borderColor: "#1565C0",
    //               }}
    //               onClick={() => onClickEdit(record.Id)}
    //             >
    //               <EditFilled /> Edit
    //             </Button>
    //           )}
    //           {expandedRow === record.Id && (
    //             <Button
    //               type="primary"
    //               color="default"
    //               variant="filled"
    //               style={{ marginRight: 10 }}
    //               onClick={() => handleCancelChanges()}
    //             >
    //               <i className="icon"></i>Cancel
    //             </Button>
    //           )}
    //           {/* <Button danger onClick={() => onClickDelete(record)}>
    //             <DeleteFilled />
    //             {expandedRow === record.Id ? "Remove All" : "Remove"}
    //           </Button> */}
    //         </div>
    //       )}
    //     </>
    //   ),
    // },
  ];

  const detailColumns = [
    {
      title: "ID",
      dataIndex: "SegmentName",
      key: "name",
      width: isTablet ? "12%" : "5%",
      render: (_: any, record: any, index: number) =>
        `${record.ParentIndex + 1}.${index + 1}`,
    },
    {
      title: "Segment Name",
      dataIndex: "SegmentName",
      key: "name",
      width: isTablet ? "18%" : "25%",
      render: (_: any, record: any) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {record.SegmentName}
        </div>
      ),
    },
    {
      title: "Recency",
      dataIndex: "Recency",
      key: "Recency",
      width: isTablet ? "28%" : "15%",
      render: (value: RFMTier, record: any) => (
        <>
          {record.SegmentName !== "Others" && isEditSection ? (
            <Select
              placeholder="Select a Level"
              defaultValue={value}
              options={RFMTierOptions}
              style={{
                width: isTablet ? "100%" : "80%",
                border: !value ? "1px solid red" : undefined,
                borderRadius: 6,
              }}
              className={getTierClass(value)}
              onChange={(newValue) =>
                handleRFMChange(record, "Recency", newValue)
              }
            />
          ) : (
            <Button className={getTierClass(value) + " w-100"}>
              {RFMTierDescriptions[value as RFMTier]}
            </Button>
          )}

          {errors[record.Id]?.Recency && (
            <div style={{ color: "red", fontSize: "12px", marginTop: 3 }}>
              <WarningFilled style={{ color: "red", fontSize: 16 }} />{" "}
              {errors[record.Id].Recency}
            </div>
          )}
        </>
      ),
    },
    {
      title: "Frequency",
      dataIndex: "Frequency",
      key: "Frequency",
      width: isTablet ? "28%" : "15%",
      render: (value: RFMTier, record: any) => {
        const duplicates = getDuplicateDetails();

        const duplicateGroup = duplicates.filter(
          (dup: any) =>
            dup.ParentIndex === record.ParentIndex &&
            dup.DetailIndex === record.DetailIndex
        );

        return (
          <>
            {duplicateGroup.length > 0 && (
              <Tooltip
                color={"white"}
                arrow={false}
                open={true}
                style={{ width: 200 }}
                title={
                  <>
                    This combination already exists. Please remove{" "}
                    <b>
                      [
                      {duplicateGroup[0]?.conflictWith?.ParentIndex +
                        1 +
                        "." +
                        (duplicateGroup[0]?.conflictWith?.DetailIndex + 1) +
                        " " +
                        duplicateGroup[0]?.conflictWith?.SegmentName}
                      ]
                    </b>{" "}
                    first or edit the current setting.
                  </>
                }
              ></Tooltip>
            )}
            {record.SegmentName !== "Others" && isEditSection ? (
              <Select
                placeholder="Select a Level"
                defaultValue={value}
                options={RFMTierOptions}
                style={{
                  width: isTablet ? "100%" : "80%",
                  border: !value ? "1px solid red" : undefined,
                  borderRadius: 6,
                }}
                className={getTierClass(value)}
                onChange={(newValue) =>
                  handleRFMChange(record, "Frequency", newValue)
                }
              />
            ) : (
              <Button className={getTierClass(value) + " w-100"}>
                {RFMTierDescriptions[value as RFMTier]}
              </Button>
            )}
            {errors[record.Id]?.Frequency && (
              <div style={{ color: "red", fontSize: "12px", marginTop: 3 }}>
                <WarningFilled style={{ color: "red", fontSize: 16 }} />{" "}
                {errors[record.Id].Frequency}
              </div>
            )}
          </>
        );
      },
    },
    {
      title: "Monetary",
      dataIndex: "Monetary",
      key: "Monetary",
      width: isTablet ? "28%" : "15%",
      render: (value: RFMTier, record: any) => (
        <>
          {record.SegmentName !== "Others" && isEditSection ? (
            <Select
              placeholder="Select a Level"
              defaultValue={value}
              options={RFMTierOptions}
              style={{
                width: isTablet ? "100%" : "80%",
                border: !value ? "1px solid red" : undefined,
                borderRadius: 6,
              }}
              className={getTierClass(value)}
              onChange={(newValue) =>
                handleRFMChange(record, "Monetary", newValue)
              }
            />
          ) : (
            <Button className={getTierClass(value) + " w-100"}>
              {RFMTierDescriptions[value as RFMTier]}
            </Button>
          )}
          {errors[record.Id]?.Monetary && (
            <div style={{ color: "red", fontSize: "12px", marginTop: 3 }}>
              <WarningFilled style={{ color: "red", fontSize: 16 }} />{" "}
              {errors[record.Id].Monetary}
            </div>
          )}
        </>
      ),
    },
    {
      title: "Action",
      key: "Action",
      width: isTablet ? "22%" : "15%",
      render: (_: any, record: any) =>
        isEditSection ? (
          <>
            {record.SegmentName !== "Others" ? (
              <Button
                className="delete-btn"
                danger
                onClick={() => handleDeleteCombination(record)}
                style={{ fontWeight: "bold" }}
              >
                <DeleteFilled /> <span>Remove</span>
              </Button>
            ) : (
              <Button type="primary" onClick={() => HandleAssign(record)}>
                <i className="icon-assign"></i> Assign
              </Button>
            )}
          </>
        ) : null,
    },
  ];

  const incompleteColumns = [
    {
      title: "Duplicate Subsegment",
      dataIndex: "DuplicateSubsegment",
      key: "DuplicateSubsegment",
      render: (text: any) => (text ? <span>• {text}</span> : ""),
    },
    {
      title: "Incomplete Entries",
      dataIndex: "IncompleteEntries",
      key: "IncompleteEntries",
      render: (text: any) => (text ? <span>• {text}</span> : ""),
    },
  ];

  // =====================
  // Validation form
  // =====================
  const validateDraft = () => {
    const newErrors: Record<
      number,
      { Recency?: string; Frequency?: string; Monetary?: string }
    > = {};

    brandMemberSegmentDraft.forEach((segment) => {
      if (!segment.BrandMemberSegmentDetails) return;

      segment.BrandMemberSegmentDetails.forEach((detail) => {
        if (!detail.Id) return;

        const error: {
          Recency?: string;
          Frequency?: string;
          Monetary?: string;
        } = {};
        if (!detail.Recency) error.Recency = "This field is required";
        if (!detail.Frequency) error.Frequency = "This field is required";
        if (!detail.Monetary) error.Monetary = "This field is required";

        if (Object.keys(error).length > 0) {
          newErrors[detail.Id] = error;
        }
      });
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // =====================
  // Modal
  // =====================
  const showModalSuccess = (type: string = "Save Setting") => {
    if (type === "Save Setting") {
      setSuccessMessage("RFM Combination settings has been saved successfully");
      setSuccessTitle("Save Settings RFM Combination");
    } else if (type === "Delete Segment") {
      setSuccessMessage("Deleted Success");
      setSuccessTitle("Your segment has been deleted successfully");
    } else if (type === "Update to Default Settings") {
      setSuccessMessage("Update to Default Settings");
      setSuccessTitle("Update to default settings has been saved successfully");
    } else if (type === "Save Member Segment") {
      setSuccessMessage("The RFM Member Segment has been saved successfully");
      setSuccessTitle("Saved Member Segment");
    } else if (type === "Save Assign") {
      const assignedSegment = brandMemberSegmentDraft.find(
        (segment) => segment.Id === assignTo
      );

      setSuccessTitle("Assignment Successful");
      setSuccessMessage(
        <>
          The segment has been successfully assigned to{" "}
          <strong>[{assignedSegment?.SegmentName ?? "Unknown Segment"}]</strong>
        </>
      );
    }

    setIsModalSuccessVisible(true);
  };

  const closeModalSuccess = () => {
    setIsModalSuccessVisible(false);
  };

  const showModalError = () => {
    setIsErrorModalVisible(true);
  };

  const closeErrorModal = () => {
    setIsErrorModalVisible(false);
  };

  // =====================
  // Reset Brand Member Segment
  // =====================

  const resetBrandMemberSegment = async () => {
    try {
      setLoading(true);

      if (!brandId) return;
      await brandMemberSegmentService.resetBrandMemberSegment(brandId);
      await sleep(2500); // wait for DB replication to catch up

      await fetchBrandData();
      setIsUpdateDefaultModalSuccessVisible(true);
      setIsConfirmationModalVisible(false);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error fetching RFM settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // Compare Active Setting with Default Setting
  // =====================
  const removeFields = (obj: any, fields: string[]) => {
    const clone = { ...obj };
    fields.forEach((field) => delete clone[field]);
    return clone;
  };

  const normalize = (arr: any[]) =>
    arr
      .filter((item) => item.Id !== -1)
      .map((item) => {
        const cleaned = removeFields(item, [
          "Id",
          "CreatedDate",
          "Count",
          "DataState",
          "SettingAccountGroupId",
          "BrandId",
        ]);

        if (cleaned.BrandMemberSegmentDetails) {
          cleaned.BrandMemberSegmentDetails =
            cleaned.BrandMemberSegmentDetails.map((detail: any) =>
              removeFields(detail, [
                "Id",
                "CreatedDate",
                "BrandMemberSegmentId",
                "Count",
                "DataState",
              ])
            ).sort((a: any, b: any) =>
              JSON.stringify(a).localeCompare(JSON.stringify(b))
            );
        }

        return cleaned;
      })
      .sort((a, b) => a.SegmentName.localeCompare(b.SegmentName));

  const areSettingsEqual = (a: any[], b: any[]): boolean => {
    const aNorm = normalize(a);
    const bNorm = normalize(b);
    return JSON.stringify(aNorm) === JSON.stringify(bNorm);
  };

  // =====================
  // Helpers
  // =====================
  const sleep = async (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const onFinishAssign = () => {
    if (!assignTo || !selectedCombationToAssign) {
      return;
    }

    setBrandMemberSegmentDraft((prevSegments: BrandMemberSegmentDraft[]) =>
      prevSegments.map((segment) => {
        if (segment.Id === assignTo) {
          return {
            ...segment,
            BrandMemberSegmentDetails: [
              ...(Array.isArray(segment.BrandMemberSegmentDetails)
                ? segment.BrandMemberSegmentDetails
                : []),
              {
                Id: Math.floor(10000 + Math.random() * 90000),
                Recency: selectedCombationToAssign.Recency,
                Frequency: selectedCombationToAssign.Frequency,
                Monetary: selectedCombationToAssign.Monetary,
                BrandMemberSegmentId: segment.Id,
                DataState: DataState.CREATED,
              },
            ],
          };
        }
        if (segment.Id === -1) {
          return {
            ...segment,
            BrandMemberSegmentDetails: (
              segment.BrandMemberSegmentDetails || []
            ).filter(
              (detail) =>
                detail.Recency !== selectedCombationToAssign.Recency ||
                detail.Frequency !== selectedCombationToAssign.Frequency ||
                detail.Monetary !== selectedCombationToAssign.Monetary
            ),
          };
        }

        return segment;
      })
    );
    showModalSuccess("Save Assign");
    setSelectedCombationToAssign(null);
    setIsAssignMember(false);
    setAssignTo(undefined);
    setHasUnsavedChanges(true);
  };

  const getCombinedIncomplete = () => {
    const duplicateCount = duplicateDetails.length;
    const incompleteEntries: {
      key: number;
      segment: any;
      errors: ErrorDetails | null;
      parentIndex: number;
      detailIndex: number;
    }[] = [];

    brandMemberSegmentDraft.forEach((segment, parentIndex) => {
      segment.BrandMemberSegmentDetails?.forEach((detail, detailIndex) => {
        if (detail.Id !== undefined && errors[detail.Id]) {
          incompleteEntries.push({
            key: detail.Id,
            segment: segment,
            errors: errors[detail.Id],
            parentIndex,
            detailIndex,
          });
        }
      });
    });

    const maxLength = Math.max(duplicateCount, incompleteEntries.length);

    return Array.from({ length: maxLength }).map((_, index) => {
      const duplicate = duplicateDetails[index];
      const incomplete = incompleteEntries[index];

      return {
        key: index,
        DuplicateSubsegment: duplicate
          ? `${duplicate.ParentIndex + 1}.${duplicate.DetailIndex + 1} ${
              duplicate.SegmentName
            }`
          : null,
        IncompleteEntries: incomplete
          ? `${incomplete.parentIndex + 1}.${incomplete.detailIndex + 1} ${
              incomplete.segment.SegmentName || "Unknown"
            } `
          : null,
      };
    });
  };

  const RFMTierOptions = Object.entries(RFMTierDescriptions).map(
    ([value, label]) => ({
      value: Number(value),
      label,
    })
  );

  const getTierClass = (value?: RFMTier) =>
    value !== undefined
      ? `select-tier select-${RFMTierDescriptions[value]
          .toLowerCase()
          .replace(" ", "-")}`
      : "select-tier select-default";

  const confirmExpandChange = () => {
    // handleSaveDraft();
    // handleCancelChanges();
    setDuplicateWarningVisible(false);
    // setSaveWarningVisible(false);
    // setHasUnsavedChanges(false);
    // setExpandedRow(null);
  };

  return (
    <>
      <div style={{ height: "85vh", overflowY: "auto" }}>
        <Card
          className="card-member-rfm-segment"
          variant="borderless"
          style={{ width: "100%", position: "relative" }}
          extra={""}
        >
          <div
            style={{
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 1000,
              padding: "12px 24px 0px 24px",
            }}
          >
            <Row
              align={"middle"}
              justify={isMobileOrTablet ? "center" : "start"}
            >
              <Col
                xs={24}
                md={24}
                xl={12}
                lg={12}
                style={{
                  fontSize: 20,
                  textAlign: isMobileOrTablet ? "left" : "left",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Member RFM Segment</span>
                <Button
                  type="primary"
                  style={{
                    marginLeft: 25,
                    backgroundColor: "#E9F4FB",
                    color: "#1565C0",
                  }}
                >
                  Last Update:{" "}
                  <strong>
                    {moment().subtract(1, "days").format("MMM DD, YYYY")}{" "}
                    23:59:59
                  </strong>
                  <Tooltip
                    placement="bottom"
                    title={
                      "The last update reflects data from the lifetime period."
                    }
                  >
                    <InfoCircleFilled
                      style={{
                        fontSize: 16,
                        marginLeft: 6,
                        color: "black",
                      }}
                    />
                  </Tooltip>
                </Button>
              </Col>
              {/* <Col
              xs={24}
              md={24}
              xl={12}
              lg={12}
              style={{ marginTop: 10, textAlign: "right" }}
            >
              <Button
                type="primary"
                style={{
                  marginLeft: 0,
                  backgroundColor: "#E9F4FB",
                  color: "#1565C0",
                }}
              >
                Last Update:{" "}
                <strong>
                  {moment().subtract(1, "days").format("MMM DD, YYYY")} 23:59:59
                </strong>
              </Button>
            </Col> */}
              {/* <Col
              xs={24}
              md={12}
              xl={12}
              lg={12}
              style={{
                marginTop: 10,
                textAlign: isMobileOrTablet ? "center" : "left",
              }}
            >
              <Button
                style={{ backgroundColor: "#1565C0", color: "white" }}
                onClick={addNewSegment}
              >
                <PlusCircleOutlined /> Add Segment
              </Button>
            </Col> */}
              <Col
                xs={24}
                md={24}
                xl={12}
                lg={12}
                style={{
                  marginTop: 10,
                  textAlign: isMobileOrTablet ? "center" : "left",
                }}
              >
                <Row justify={"end"} gutter={[8, 0]} style={{ gap: 8 }}>
                  <Col
                    span={24}
                    style={{
                      display: "flex",
                      justifyContent: isMobileOrTablet ? "center" : "flex-end",
                      gap: 8,
                    }}
                  >
                    {isEditSection ? (
                      <Button
                        type="primary"
                        color="default"
                        variant="filled"
                        size={isMobileOrTablet ? "small" : "large"}
                        style={{
                          fontWeight: 700,
                          width: isMobileOrTablet ? "100%" : 210,
                          fontSize: isMobileOrTablet ? "12px !important" : 14,
                        }}
                        onClick={() =>
                          !hasUnsavedChanges
                            ? handleCancelChanges()
                            : setIsCancelWarningVisible(true)
                        }
                      >
                        <CloseCircleOutlined />
                        Cancel
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        danger
                        size={isMobileOrTablet ? "middle" : "large"}
                        block
                        disabled={areSettingsEqual(
                          originalBrandMemberSegment,
                          defaultSetting
                        )}
                        onClick={() => setIsConfirmationModalVisible(true)}
                        style={{
                          fontWeight: 700,
                          width: isMobileOrTablet ? "100%" : 210,
                          fontSize: isMobileOrTablet ? "12px !important" : 14,
                        }}
                      >
                        <i className="icon-replay"></i>
                        <span
                          style={{
                            fontSize: isMobileOrTablet ? "12px !important" : 14,
                          }}
                        >
                          Update to Default
                        </span>
                      </Button>
                    )}
                    {isEditSection ? (
                      <Button
                        type="primary"
                        size={isMobileOrTablet ? "middle" : "large"}
                        block
                        style={{
                          backgroundColor: isDisabled ? "#B0BEC5" : "#1565C0",
                          color: isDisabled ? "#757575" : "white",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          fontWeight: 700,
                          width: isMobileOrTablet ? "100%" : 210,
                          fontSize: isMobileOrTablet ? 12 : 14,
                        }}
                        disabled={isDisabled}
                        onClick={() => handleSaveDraft()}
                      >
                        <SaveFilled />
                        <span
                          style={{
                            fontSize: isMobileOrTablet ? 12 : 14,
                          }}
                        >
                          {" "}
                          Save Member Segment
                        </span>
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        size={isMobileOrTablet ? "middle" : "large"}
                        block
                        style={{
                          backgroundColor: "#1565C0",
                          color: "white",
                          cursor: "pointer",
                          opacity: 1,
                          fontWeight: 700,
                          fontSize: isMobileOrTablet ? 12 : 14,
                          width: isMobileOrTablet ? "100%" : 210,
                          gap: 4,
                        }}
                        onClick={() => setIsEditSection(true)}
                      >
                        <EditOutlined />
                        <span
                          style={{
                            fontSize: isMobileOrTablet ? 12 : 14,
                          }}
                        >
                          {" "}
                          Edit Member Segment
                        </span>
                      </Button>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider style={{ margin: "12px 0px" }} />
          </div>
          <div className="content-member-rfm-segment">
            <Table
              style={{ width: "100%" }}
              columns={segmentColumns}
              dataSource={brandMemberSegmentDraft}
              rowKey="Id"
              scroll={{ x: "max-content" }}
              rowClassName={"segment-cell"}
              loading={loading}
              pagination={false}
              expandable={{
                expandedRowKeys: expandedRow,
                onExpand: handleExpand,
                expandIcon: ({ expanded, onExpand, record }) => (
                  <div
                    className={`custom-expand-icon ${
                      expanded ? "expanded" : ""
                    }`}
                    onClick={(e) => onExpand(record, e)}
                  >
                    {expanded ? (
                      <MinusSquareOutlined />
                    ) : (
                      <PlusSquareOutlined />
                    )}
                  </div>
                ),
                expandedRowRender: (record, segmentIndex) => {
                  const duplicates = getDuplicateDetails();

                  const details = (record.BrandMemberSegmentDetails ?? []).map(
                    (detail, detailIndex) => ({
                      ...detail,
                      SegmentName: record.SegmentName,
                      ParentIndex: segmentIndex,
                      DetailIndex: detailIndex,
                    })
                  );

                  return (
                    <>
                      <Table
                        scroll={{ x: "max-content" }}
                        className="detail-table"
                        style={{ width: "100%" }}
                        rowKey={(record) =>
                          `${record.ParentIndex}-${record.DetailIndex}`
                        }
                        columns={detailColumns}
                        dataSource={details}
                        pagination={false}
                        rowClassName={(record) => {
                          const isDuplicate = duplicates.some(
                            (dup: any) =>
                              dup.ParentIndex === segmentIndex &&
                              dup.DetailIndex === record.DetailIndex
                          );
                          return isDuplicate ? "error-row" : "";
                        }}
                        // expandable={{
                        //   expandedRowClassName: "error-row",
                        //   expandedRowKeys: alwaysExpandedDetailKeys,
                        //   expandedRowRender: (record) => {
                        //     const duplicateGroup = duplicates.filter(
                        //       (dup: any) =>
                        //         dup.ParentIndex === segmentIndex &&
                        //         dup.DetailIndex === record.DetailIndex
                        //     );
                        //     if (duplicateGroup.length > 0) {
                        //       return (
                        //         <div
                        //           style={{
                        //             backgroundColor: "#FFFFFF",
                        //             padding: "12px",
                        //             borderRadius: "8px",
                        //             border: "1px solid #F87171",
                        //             display: "flex",
                        //             alignItems: "center",
                        //             gap: "8px",
                        //             color: "#E61D2B",
                        //           }}
                        //         >
                        //           <WarningFilled
                        //             style={{ color: "red", fontSize: 18 }}
                        //           />
                        //           <span>
                        //             This combination already exists. Please
                        //             remove{" "}
                        //             <b>
                        //               [
                        //               {duplicateGroup[0].conflictWith
                        //                 .ParentIndex +
                        //                 1 +
                        //                 "." +
                        //                 (duplicateGroup[0].conflictWith
                        //                   .DetailIndex +
                        //                   1) +
                        //                 " " +
                        //                 duplicateGroup[0].conflictWith
                        //                   .SegmentName}
                        //               ]
                        //             </b>{" "}
                        //             first or edit the current setting.
                        //           </span>
                        //         </div>
                        //       );
                        //     }

                        //     return null;
                        //   },
                        //   rowExpandable: (record) =>
                        //     duplicates.some(
                        //       (dup: any) =>
                        //         dup.ParentIndex === segmentIndex &&
                        //         dup.DetailIndex === record.DetailIndex
                        //     ),
                        //   showExpandColumn: false,
                        // }}
                        footer={() => (
                          <>
                            {record.Id !== -1 && isEditSection && (
                              <Row justify="space-between" align="middle">
                                <Col>
                                  <Button
                                    type="default"
                                    style={{
                                      color: "#1565C0",
                                      borderColor: "#1565C0",
                                      fontWeight: "bold",
                                    }}
                                    onClick={() =>
                                      record.Id !== undefined &&
                                      addNewCombination(record.Id)
                                    }
                                  >
                                    <PlusCircleOutlined /> Add New Combination
                                  </Button>
                                </Col>
                                <Col>
                                  {/* <Button
                          style={{
                            backgroundColor: hasUnsavedChanges
                              ? "#1565C0"
                              : "#B0BEC5",
                            color: "white",
                            cursor: hasUnsavedChanges
                              ? "pointer"
                              : "not-allowed",
                            opacity: hasUnsavedChanges ? 1 : 0.6,
                          }}
                          disabled={!hasUnsavedChanges}
                          type="primary"
                          onClick={() => handleSaveDraft()}
                        >
                          <i className="icon-save"></i> Save Draft
                        </Button> */}
                                </Col>
                              </Row>
                            )}
                          </>
                        )}
                      />
                    </>
                  );
                },
              }}
            />
            <div
              style={{
                position: "sticky",
                bottom: "0px",
                display: "flex",
                zIndex: 1000,
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                fontSize: "16px",
                background: "white",
              }}
            >
              <div>
                Total Combination:{" "}
                <span style={{ fontWeight: "bold" }}>
                  {brandMemberSegmentDraft
                    ?.filter((segment) => segment.Id !== -1)
                    .reduce(
                      (total, segment) =>
                        total +
                        (segment.BrandMemberSegmentDetails?.length || 0),
                      0
                    )}{" "}
                  Combination{" "}
                </span>
              </div>

              {/* Right: Pagination */}
              <Pagination
                pageSize={pageSize}
                current={currentPage}
                total={brandMemberSegmentDraft.length}
                onChange={(page) => setCurrentPage(page)}
              />
            </div>
            {/* <Divider /> */}
            <Row justify="center" align="middle" gutter={[10, 10]} wrap>
              {/* <Col xs={24} md={24} xl={12}>
            <div style={{ fontSize: "16px" }}>
              Total Combination:{" "}
               <span style={{ fontWeight: "bold" }}>
                {brandMemberSegmentDraft?.reduce(
                  (total, segment) =>
                    total + (segment.BrandMemberSegmentDetails?.length || 0),
                  0
                )}{" "}
                Combination{" "}
              </span>
              <span style={{ color: "red" }}>
                {duplicateDetails.length} Duplicate
              </span> 
            </div>
            <span
              style={{
                cursor: duplicateDetails.length > 0 ? "pointer" : "not-allowed",
                color: duplicateDetails.length > 0 ? "#1677ff" : "gray",
                opacity: duplicateDetails.length > 0 ? 1 : 0.5,
              }}
              onClick={() => {
                if (duplicateDetails.length > 0) {
                  setIsDuplicateRFMVisible(true);
                }
              }}
            >
              View Details <ArrowRightOutlined />
            </span> 
          </Col> */}

              <Col xs={24} md={24} xl={12}>
                {/* <Row justify="end" gutter={[10, 10]} wrap>
              <Col xs={24} md={24} xl={12}>
                <Button type="primary" danger size="large" block disabled>
                  <i className="icon-replay"></i>
                  <span style={{ fontSize: "14px" }}>UPDATE TO DEFAULT</span>
                </Button>
              </Col>
              <Col xs={24} md={24} xl={12}>
                <Button
                  type="primary"
                  size="large"
                  block
                  style={{
                    backgroundColor: isDisabled ? "#B0BEC5" : "#1565C0",
                    color: isDisabled ? "#757575" : "white",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.6 : 1,
                  }}
                  disabled={isDisabled}
                  onClick={() => handleSaveSettings()}
                >
                  <i className="icon-save"></i>
                  <span style={{ fontSize: "14px" }}> SAVE SETTINGS</span>
                </Button>
              </Col>
            </Row> */}
              </Col>
            </Row>
          </div>
        </Card>
        <CustomModal
          type="confirm"
          title="Delete Segment Tier"
          message="Are you sure you want to delete this segment tier?"
          isVisible={isDeleteModalVisible}
          onClose={() => setDeleteModalVisible(false)}
          onConfirm={confirmDelete}
        />
        <CustomModal
          type="confirm"
          title="Delete Combination"
          message="Are you sure you want to delete this combination?"
          isVisible={deleteModalDetailVisible}
          onClose={() => setDeleteModalDetailVisible(false)}
          onConfirm={confirmDeleteDetail}
        />
        <CustomModal
          type="warning"
          title="Save Your Combination"
          message="You have unsaved changes. Save now to avoid losing your updates"
          isVisible={isSaveWarningVisible}
          onClose={() => setSaveWarningVisible(false)}
          onConfirm={handleSaveDraft}
        />
        <CustomModal
          type="warning-incomplete"
          title="Incomplete Configuration"
          message="You have an incomplete configuration. Continue editing to avoid losing your updates."
          isVisible={isDuplicateWarningVisible}
          onClose={() => handleCancelChanges()}
          onConfirm={confirmExpandChange}
        />
        <CustomModal
          type="success"
          title={successTitle}
          message={successMessage}
          isVisible={isModalSuccessVisible}
          onClose={closeModalSuccess}
          autoClose
          duration={3000}
        />
        <CustomModal
          type="error"
          title="Operation Failed"
          message="Something went wrong. Please try again."
          isVisible={isErrorModalVisible}
          onClose={closeErrorModal}
          autoClose
          duration={3000}
        />
        <Modal
          title=""
          open={isDuplicateRFMVisible}
          width={550}
          onCancel={() => setIsDuplicateRFMVisible(false)}
          maskClosable={false}
          closable={false}
          footer={
            <Row gutter={16}>
              <Col span={12}>
                <Button
                  key="cancel"
                  className="w-100"
                  style={{ height: 45 }}
                  onClick={() => {
                    setIsCancelWarningVisible(true);
                    setIsDuplicateRFMVisible(false);
                  }}
                >
                  Cancel
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  key="edit"
                  type="primary"
                  style={{
                    backgroundColor: "#1565C0",
                    color: "white",
                    height: 45,
                  }}
                  onClick={() => setIsDuplicateRFMVisible(false)}
                  className="w-100"
                >
                  Continue Editing
                </Button>
              </Col>
            </Row>
          }
        >
          <Row justify={"center"} style={{ marginTop: 25 }}>
            <WarningFilled style={{ color: "red", fontSize: 100 }} />{" "}
          </Row>
          <Row justify={"center"} style={{ fontSize: 16, fontWeight: "bold" }}>
            Incomplete Configuration
          </Row>
          <Row justify={"center"}>
            Your configuration is incomplete. Please resolve these issues before
            saving{" "}
          </Row>
          <Table
            style={{ marginTop: 10 }}
            columns={incompleteColumns}
            dataSource={getCombinedIncomplete()}
            rowKey="key"
            pagination={false}
          />
        </Modal>
        <Modal
          title="Assign Member Segment"
          open={isAssignMember}
          width={700}
          onCancel={() => setIsAssignMember(false)}
          footer={
            <>
              <Divider />
              <Row gutter={16}>
                <Col span={12}>
                  <Button
                    key="cancel"
                    className="w-100"
                    style={{ height: 52 }}
                    onClick={() => setIsAssignMember(false)}
                  >
                    Cancel
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    className="w-100"
                    key="edit"
                    type="primary"
                    disabled={!assignTo}
                    style={{
                      backgroundColor: assignTo ? "#1565C0" : "#B0BEC5",
                      color: "white",
                      height: 52,
                    }}
                    onClick={onFinishAssign}
                  >
                    Apply
                  </Button>
                </Col>
              </Row>
            </>
          }
        >
          <Row>
            <Col span={12}>Member Segment</Col>
            <Col span={12}>
              <Select
                placeholder={"Select a Member Segment"}
                value={assignTo}
                onChange={(value) => setAssignTo(value)}
                options={brandMemberSegmentDraft
                  .filter((segment) => segment.Id !== -1)
                  .map((segment, index) => ({
                    label: index + 1 + ". " + segment.SegmentName,
                    value: segment.Id,
                  }))}
                style={{ width: "100%" }}
                onDropdownVisibleChange={(open) => setIsDropdownOpen(open)}
              />
            </Col>
          </Row>
          {isDropdownOpen && <Row style={{ marginTop: 250 }}></Row>}
        </Modal>
        <Modal
          title=""
          open={isCancelWarningVisible}
          width={550}
          maskClosable={false}
          closable={false}
          footer={
            <Row gutter={16}>
              <Col span={12}>
                <Button
                  key="cancel"
                  className="w-100"
                  style={{ height: 45 }}
                  onClick={() => handleCancelChanges()}
                >
                  Cancel
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  key="edit"
                  type="primary"
                  style={{
                    backgroundColor: "#1565C0",
                    color: "white",
                    height: 45,
                  }}
                  onClick={() => handleSaveDraft()}
                  className="w-100"
                >
                  Save Member Segment
                </Button>
              </Col>
            </Row>
          }
        >
          <Row justify={"center"} style={{ marginTop: 25 }}>
            <WarningFilled style={{ color: "#EEB711", fontSize: 100 }} />{" "}
          </Row>
          <Row justify={"center"} style={{ fontSize: 16, fontWeight: "bold" }}>
            Save Your Member Segment
          </Row>
          <Row justify={"center"}>
            You have unsaved member segment. Save now to avoid losing your
            updates{" "}
          </Row>
        </Modal>
        <Modal
          title=""
          open={isConfirmationModalVisible}
          width={550}
          height={300}
          onCancel={() => setIsConfirmationModalVisible(false)}
          maskClosable={false}
          closable={false}
          centered
          footer={
            <>
              <Divider />
              <Row gutter={16}>
                <Col span={12}>
                  <Button
                    size="large"
                    key="cancel"
                    className="w-100"
                    style={{ height: 52 }}
                    onClick={() => {
                      setIsConfirmationModalVisible(false);
                    }}
                  >
                    Cancel
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    size="large"
                    key="edit"
                    type="primary"
                    style={{
                      backgroundColor: "#1565C0",
                      color: "white",
                      height: 52,
                    }}
                    onClick={() => resetBrandMemberSegment()}
                    className="w-100"
                  >
                    Yes
                  </Button>
                </Col>
              </Row>
            </>
          }
        >
          <Row justify={"center"} style={{ marginTop: 25 }}>
            <WarningFilled style={{ color: "#EEB711", fontSize: 100 }} />{" "}
          </Row>{" "}
          <Row justify={"center"} style={{ fontSize: 16, fontWeight: "bold" }}>
            Update to Default Settings
          </Row>
          <Row justify={"center"}>
            Are you sure you want to update to default?{" "}
          </Row>
        </Modal>
        <CustomModal
          type="success"
          title="Update to Default Settings"
          message="Update to default settings has been saved successfully"
          isVisible={isUpdateDefaultModalSuccessVisible}
          onClose={() => setIsUpdateDefaultModalSuccessVisible(false)}
          autoClose
          duration={3000}
        />
      </div>
    </>
  );
};

export default RFMCombination;
